import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, X, RefreshCw, Loader2, SortAsc, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import CollectionsDashboard from '@/components/collections/CollectionsDashboard';
import ArAgingWidget from '@/components/collections/ArAgingWidget';
import OutstandingCustomerCard from '@/components/collections/OutstandingCustomerCard';
import CollectionHistoryCard from '@/components/collections/CollectionHistoryCard';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { cur } from '@/lib/currency';

const PERIOD_FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: 'week', label: 'الأسبوع' },
  { key: 'month', label: 'الشهر' },
  { key: 'last_month', label: 'الشهر الماضي' },
];

const SORT_OPTIONS = [
  { key: 'amount_desc', label: 'الأعلى مبلغاً' },
  { key: 'overdue_first', label: 'الأكثر تأخراً' },
];

// Aging-bucket filter chips for the outstanding tab — the collections
// equivalent of the sales-order status chips. `null` = الكل (no filter).
// Keys match the agingFilter logic in `displayedCustomers` and the bucket
// keys returned by the aging endpoint (aging.counts[key]).
const AGING_FILTERS = [
  { key: null, label: 'الكل' },
  { key: 'current', label: 'غير مستحق' },
  { key: 'b30', label: '1-30 يوم' },
  { key: 'b60', label: '31-60 يوم' },
  { key: 'b90', label: '61-90 يوم' },
  { key: 'b90plus', label: '90+ يوم' },
];

export default function Collections() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();

  // View toggle
  const [view, setView] = useState('outstanding'); // 'outstanding' | 'history'

  // Dashboard
  const [stats, setStats] = useState(null);
  const [aging, setAging] = useState(null);

  // Outstanding
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [sortBy, setSortBy] = useState('amount_desc');

  // History
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [histTotal, setHistTotal] = useState(0);
  const [histPage, setHistPage] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('all');

  // Search
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);

  const [agingFilter, setAgingFilter] = useState(null); // active aging bucket drill-down

  // ── Load dashboard stats + aging on mount ────────────────────────────
  // Prefer SellGo-scoped endpoints because the v4 collections endpoints
  // ignore user scoping and return company-wide figures.
  const loadDashboard = useCallback(async () => {
    const [statsRes, agingRes] = await Promise.allSettled([
      (async () => {
        try {
          return await api.get('/sellgo_coll_dashboard');
        } catch {
          return odooSession?.role === 'manager' ? await api.get('/collections/dashboard') : null;
        }
      })(),
      (async () => {
        try {
          return await api.get('/sellgo_coll_aging');
        } catch {
          return odooSession?.role === 'manager' ? await api.get('/collections/aging') : null;
        }
      })(),
    ]);
    if (statsRes.status === 'fulfilled' && statsRes.value?.success) setStats(statsRes.value.data);
    if (agingRes.status === 'fulfilled' && agingRes.value?.success) setAging(agingRes.value.data);
  }, [odooSession?.role]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // ── Load outstanding customers ────────────────────────────────────────
  const loadOutstanding = useCallback(async (q = search, sort = sortBy) => {
    setLoadingCustomers(true);
    // The backend exposes outstanding INVOICES, not customers, but the page
    // wants a per-customer rollup. We fetch outstanding invoices and group.
    // Prefer the scoped endpoint; fall back to legacy if not deployed.
    let res;
    try {
      res = await api.get('/sellgo_coll_outstanding', { limit: 500 });
    } catch {
      res = odooSession?.role === 'manager' ? await api.get('/collections/outstanding', { limit: 500 }) : null;
    }
    if (res?.success) {
      // Roll invoices up to one row per customer
      const map = new Map();
      for (const inv of (res.data || [])) {
        const pid = Array.isArray(inv.partner_id) ? inv.partner_id[0] : inv.partner_id;
        const pname = Array.isArray(inv.partner_id) ? inv.partner_id[1] : '';
        if (!pid) continue;
        const row = map.get(pid) || {
          id: pid, partner_id: pid, name: pname,
          total_due: 0, invoices_count: 0, oldest_days: 0,
        };
        row.total_due += Number(inv.amount_residual || 0);
        row.invoices_count += 1;
        row.oldest_days = Math.max(row.oldest_days, Number(inv.overdue_days || 0));
        map.set(pid, row);
      }
      let rows = Array.from(map.values());
      // Optional client-side search by name
      if (q) {
        const ql = q.toLowerCase();
        rows = rows.filter(r => (r.name || '').toLowerCase().includes(ql));
      }
      // Sort
      if (sort === 'amount_desc') rows.sort((a, b) => b.total_due - a.total_due);
      else if (sort === 'oldest') rows.sort((a, b) => b.oldest_days - a.oldest_days);
      else rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCustomers(rows.slice(0, 100));
    }
    setLoadingCustomers(false);
  }, [search, sortBy]);

  // ── Load history ──────────────────────────────────────────────────────
  const loadHistory = useCallback(async (q = search, period = periodFilter, pg = histPage) => {
    setLoadingPayments(true);
    // Translate the period chip into a date_from/date_to range, filtered by the
    // payment-received date. Uses LOCAL calendar dates (Y/M/D), NOT
    // toISOString() — the latter is UTC, so "today" would be wrong for users
    // in Cairo (UTC+2/+3) in the early hours.
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = new Date();
    let date_from, date_to;
    if (period === 'today') {
      date_from = date_to = iso(today);
    } else if (period === 'yesterday') {
      const d = new Date(today); d.setDate(d.getDate() - 1);
      date_from = date_to = iso(d);
    } else if (period === 'week') {
      const d = new Date(today); d.setDate(d.getDate() - 6); // last 7 days incl. today
      date_from = iso(d); date_to = iso(today);
    } else if (period === 'month') {
      date_from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
      date_to = iso(today);
    } else if (period === 'last_month') {
      const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0); // day 0 = last day of prev month
      date_from = iso(firstPrev); date_to = iso(lastPrev);
    }
    // period === 'all' → no date filter
    const params = { page: pg, limit: 30 };
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    // Reps see only their own collections. Supervisors/managers must NOT
    // pin their own uid here — that would scope the list to themselves and
    // hide their team. The backend handles team/all scoping server-side.
    if (odooSession?.role === 'rep' && odooSession?.uid) {
      params.salesman_uid = odooSession.uid;
    }
    // Prefer the scoped endpoint; fall back to legacy if not deployed.
    let res;
    try {
      res = await api.get('/sellgo_coll_history', params);
    } catch {
      res = odooSession?.role === 'manager' ? await api.get('/collections/payments', params) : null;
    }
    if (res?.success) {
      let rows = res.data || [];
      // Optional client-side search (by partner name or payment name)
      if (q) {
        const ql = q.toLowerCase();
        rows = rows.filter(r => {
          const pname = Array.isArray(r.partner_id) ? r.partner_id[1] : '';
          return (pname || '').toLowerCase().includes(ql) ||
                 (r.name || '').toLowerCase().includes(ql);
        });
      }
      setPayments(rows);
      setHistTotal(res.total || rows.length);
    }
    setLoadingPayments(false);
  }, [search, periodFilter, histPage]);

  useEffect(() => {
    if (view === 'outstanding') loadOutstanding();
    else loadHistory();
  }, [view, sortBy, periodFilter, histPage]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (view === 'outstanding') loadOutstanding(val, sortBy);
      else loadHistory(val, periodFilter, 0);
    }, 400);
  };

  const handleCollect = (customer) => {
    navigate(`/collections/new?customer_id=${customer.id}&customer_name=${encodeURIComponent(customer.name)}`);
  };

  const handleShare = (payment) => {
    const text = `إيصال تحصيل ${payment.name}\nالعميل: ${Array.isArray(payment.partner_id) ? payment.partner_id[1] : payment.partner_id}\nالمبلغ: ${Number(payment.amount).toLocaleString('ar-EG')} ${cur()}`;
    if (navigator.share) navigator.share({ title: payment.name, text });
    else navigator.clipboard.writeText(text);
  };

  // Filter customers by aging bucket
  const displayedCustomers = agingFilter
    ? customers.filter(c => {
        if (agingFilter === 'current') return c.max_overdue_days === 0;
        if (agingFilter === 'b30') return c.max_overdue_days > 0 && c.max_overdue_days <= 30;
        if (agingFilter === 'b60') return c.max_overdue_days > 30 && c.max_overdue_days <= 60;
        if (agingFilter === 'b90') return c.max_overdue_days > 60 && c.max_overdue_days <= 90;
        if (agingFilter === 'b90plus') return c.max_overdue_days > 90;
        return true;
      })
    : customers;

  const totalPages = Math.ceil(histTotal / 30);

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* ── Sticky search + filter bar ── */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 pt-3 pb-2 space-y-2 shadow-sm">
        {/* View toggle */}
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-xl p-1 flex-1">
            <button onClick={() => setView('outstanding')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${view === 'outstanding' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>
              المستحقات
            </button>
            <button onClick={() => setView('history')}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${view === 'history' ? 'bg-card shadow text-primary' : 'text-muted-foreground'}`}>
              التحصيلات المسجلة
            </button>
          </div>
          <button onClick={() => navigate('/collections/new')}
            className="bg-secondary text-white rounded-xl px-3 py-2 flex items-center gap-1 text-xs font-bold flex-shrink-0 shadow-sm">
            <Plus className="w-3.5 h-3.5" /> تحصيل جديد
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="بحث برقم الفاتورة أو اسم العميل..." className="pr-10 bg-card rounded-xl text-sm" />
          {search && (
            <button onClick={() => handleSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Period filters (history only) or sort (outstanding only) */}
        {view === 'history' ? (
          <div className="flex items-center gap-1.5">
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar flex-1">
              {PERIOD_FILTERS.map(f => (
                <button key={f.key} onClick={() => { setPeriodFilter(f.key); setHistPage(0); }}
                  className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${periodFilter === f.key ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={() => navigate('/collections/print/today')}
              title="طباعة تحصيلات اليوم"
              className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-card border border-border text-primary flex-shrink-0">
              <Printer className="w-3.5 h-3.5" /> طباعة اليوم
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {/* Aging-bucket filter chips (like the sales-order status chips) */}
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              {AGING_FILTERS.map(f => {
                const active = (agingFilter || null) === f.key;
                const cnt = f.key ? Number(aging?.counts?.[f.key]) || 0 : null;
                return (
                  <button key={f.key || 'all'} onClick={() => setAgingFilter(f.key)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${active ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'}`}>
                    {f.label}{cnt ? ` ${cnt}` : ''}
                  </button>
                );
              })}
            </div>
            {/* Sort chips */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
              <SortAsc className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              {SORT_OPTIONS.map(s => (
                <button key={s.key} onClick={() => { setSortBy(s.key); loadOutstanding(search, s.key); }}
                  className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${sortBy === s.key ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Dashboard strip */}
        <CollectionsDashboard
          stats={stats}
          onOverdueClick={() => { setView('outstanding'); setSortBy('overdue_first'); loadOutstanding(search, 'overdue_first'); }}
        />

        {/* AR Aging */}
        <ArAgingWidget
          aging={aging}
          onBucketClick={key => { setView('outstanding'); setAgingFilter(key); }}
        />

        {/* Content */}
        {view === 'outstanding' && (
          <div className="space-y-3">
            {agingFilter && (
              <p className="text-xs text-muted-foreground font-medium">
                تصفية: <span className="text-primary font-bold">{agingFilter}</span> • {displayedCustomers.length} عميل
              </p>
            )}
            {loadingCustomers ? (
              <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : displayedCustomers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-4xl mb-3">🎉</p>
                <p className="font-bold text-foreground">لا توجد مستحقات</p>
                <p className="text-sm text-muted-foreground mt-1">جميع الفواتير محصلة</p>
              </div>
            ) : (
              displayedCustomers.map(c => (
                <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <OutstandingCustomerCard customer={c} onCollect={handleCollect} />
                </motion.div>
              ))
            )}
          </div>
        )}

        {view === 'history' && (
          <div className="space-y-3">
            {/* Summary */}
            {payments.length > 0 && (
              <div className="bg-secondary/5 border border-secondary/20 rounded-2xl px-4 py-3 flex justify-between items-center">
                <p className="text-sm text-muted-foreground">{histTotal.toLocaleString('ar-EG')} تحصيل</p>
                <p className="font-bold text-secondary text-lg">
                  {payments.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString('ar-EG', { minimumFractionDigits: 2 })} {cur()}
                </p>
              </div>
            )}

            {loadingPayments ? (
              <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-4xl mb-3">💳</p>
                <p className="font-bold text-foreground">لا توجد تحصيلات</p>
              </div>
            ) : (
              <>
                {payments.map((p, i) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}>
                    <CollectionHistoryCard payment={p} onShare={handleShare} showRep={odooSession?.role !== 'rep'} />
                  </motion.div>
                ))}
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <Button variant="outline" size="sm" disabled={histPage === 0} onClick={() => setHistPage(p => p - 1)} className="rounded-xl">
                      السابق
                    </Button>
                    <span className="text-xs text-muted-foreground">صفحة {histPage + 1} من {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={histPage + 1 >= totalPages} onClick={() => setHistPage(p => p + 1)} className="rounded-xl">
                      التالي
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}